
import { mergeMap } from 'rxjs/operators'
import { combineEpics } from 'redux-observable'
import { merge, empty, concat, of, from } from 'rxjs'
import * as moment from 'moment'
import { preparePeergroupQuery } from 'helpers/peergroupsHelpers'
import { formSubmitFailedActionCreator, formSubmittedSuccessfullyActionCreator, SUBMIT_FORM } from 'actions/formActions'
import { updatePeergroupRelationActionCreator } from 'actions/peergroupActions'
import { CatchFunction, getResponseFormErrors, processTUCIError } from 'helpers/ajaxHelpers'
import { AjaxError } from 'rxjs/ajax'
import { FormErrorsResponse } from 'interfaces/component'
import { State } from 'interfaces/state'
import { Action } from 'actions'
import { startSubmit, stopSubmit } from 'redux-form'
import {
  FORM_CARS_EDIT,
  FORM_CONTACT_EDIT,
  FORM_CREATE_CUSTOM_PEER_GROUP,
  FORM_CREATE_INCOME_TRACKER,
  FORM_CREATE_SPENDING_TRACKER,
  FORM_DEVICE_VERIFICATION,
  FORM_EDIT_CUSTOM_PEER_GROUP,
  FORM_EDIT_INCOME_TRANSACTION,
  FORM_EDIT_SPENDING_TRANSACTION,
  FORM_HOMES_EDIT,
  FORM_LOGIN,
  FORM_MANUAL_ACCOUNT,
  FORM_ONBOARDING_PROPERTY,
  FORM_ONBOARDING_REGISTER,
  FORM_PASSWORD_CHANGE,
  FORM_PASSWORD_CODE_SEND,
  FORM_PASSWORD_RESET,
  FORM_MEMBER_PROFILE,
  FORM_SETTINGS_CREDIT,
  FORM_SETTINGS_CREDIT_EXTENDED,
  FORM_REDEMPTION,
  FORM_SOCIAL_PROFILE,
  FORM_EDIT_TRANSACTIONS_TRANSACTION,
} from 'constants/forms'
import {
  CHANGE_PASSWORD_URL,
  CREATE_CAR_URL,
  CREATE_HOME_URL,
  CREATE_INCOME_TRACKER_URL,
  CREATE_MANUAL_ACCOUNT_URL,
  CREATE_MEMBER_URL,
  CREATE_PEER_GROUP_URL,
  CREATE_SPENDING_TRACKER_URL,
  EDIT_MANUAL_ACCOUNT_URL,
  FormUrls,
  LOGIN_MEMBER_URL,
  PATCH_CAR_URL,
  PATCH_HOME_URL,
  PATCH_MEMBER_INFO_URL,
  PATCH_TRANSACTION_URL,
  RESET_PASSWORD_URL,
  SEND_PASSWORD_RESET_CODE_URL,
  SET_MEMBER_ASSETS_URL,
  SET_MEMBER_CREDIT_EXTENDED_URL,
  SET_MEMBER_CREDIT_URL,
  UPDATE_INCOME_TRACKER_URL,
  UPDATE_PEER_GROUP_URL,
  UPDATE_SPENDING_TRACKER_URL,
  CREATE_REDEMPTION_URL,
  POST_SOCIAL_PROFILE_URL,
} from 'interfaces/ajax'
import { Epic } from 'interfaces/middleware'
import { AnyDict } from 'interfaces'
import { resolveCategory } from 'helpers/categoriesHelpers'
import { captureWarning, getErrorType, NO_FORM_HANDLER_FOUND } from 'helpers/errorHelpers'
import { VALIDATION_ERROR_STATUS_CODE } from 'constants/ajax'
import { NEW_PROPERTY_ID } from 'constants/member'
import { setUnexpectedErrorActionCreator } from 'actions/globalActions'
import { PropertyType, RawHomeFormValues } from 'interfaces/property'
import { MoneyflowType, FullMoneyflowType, PatchTransactionAPIPayload } from 'interfaces/moneyflow'
import { CompositeAction } from 'interfaces/action'
import { UrlWithId } from 'interfaces/ajax/base'
import { FormNameType, TransactionEditFormValues } from 'interfaces/form'
import { DATE_FORMAT_ISO_FULL, DATE_FORMAT_US } from 'constants/datetime'
import { parseCurrency } from 'helpers/formHelpers'
import { RawRegisterFormValues, RegisterFormValues } from 'interfaces/onboarding'
import { createManualBalanceAccountSelector, linkageSessionSelector } from 'selectors/linkageSelectors'
import { ManualAccountFormData, RawManualAccountFormData } from 'interfaces/linkage'
import { post } from 'helpers/observableAjaxHelpers'
import { RedemptionFormValues, PatchRedemptionFormValues } from 'interfaces/bounty'
import { NO_DIGITS_REGEX } from 'constants/bounty'
import { ImageId } from 'interfaces/media'
import { pushNotificationsPlayerIdSelector } from 'selectors/pushNotifications'
import { getItemFromCollection } from 'helpers/normalizeHelpers'
import { TransactionRecatFloatingPane } from 'interfaces/floatingPanes'
import { byType } from 'rxOperators/byType'
import { mergeMapSafe } from 'rxOperators/mergeMapSafe'
import { catchSafe } from 'rxOperators/catchSafe'

type GetFormUrlFunction = (state: State, values: AnyDict) => FormUrls | null

type TrackerChangeUrl =
  | typeof CREATE_INCOME_TRACKER_URL
  | typeof CREATE_SPENDING_TRACKER_URL
  | UrlWithId<typeof UPDATE_SPENDING_TRACKER_URL | typeof UPDATE_INCOME_TRACKER_URL>

const createTrackerGetUrl = (type: MoneyflowType) => (state: State, values: AnyDict): TrackerChangeUrl | null => {
  const { trackers } = state.data[type]
  if (!trackers) {
    return null
  }

  const tracker = trackers.byKey[values.category]
  const isSpending = type === 'spending'
  const updateUrl = isSpending ? UPDATE_SPENDING_TRACKER_URL : UPDATE_INCOME_TRACKER_URL
  const createUrl = isSpending ? CREATE_SPENDING_TRACKER_URL : CREATE_INCOME_TRACKER_URL

  return tracker ?
    { url: updateUrl, params: { id: tracker.id }} :
    createUrl
}

type PropertyChangeUrl =
  | typeof CREATE_HOME_URL
  | typeof CREATE_CAR_URL
  | UrlWithId<typeof PATCH_HOME_URL | typeof PATCH_CAR_URL>

const createPropertyGetUrl = (propertyType: PropertyType) => (state: State): PropertyChangeUrl | null => {
  const { editId } = state.app.settings[propertyType]
  if (!editId) {
    return null
  }

  const forHome = propertyType === 'home'
  const createUrl = forHome ? CREATE_HOME_URL : CREATE_CAR_URL
  const patchUrl = forHome ? PATCH_HOME_URL : PATCH_CAR_URL

  return editId === NEW_PROPERTY_ID ? createUrl : { url: patchUrl, params: { id: editId }}
}

const getUpdatePeergroupUrl = (state: State): UrlWithId<typeof UPDATE_PEER_GROUP_URL> | null => {
  const { formMode } = state.app.sidebar.peergroupForm
  if (formMode.mode === 'edit') {
    return {
      url: UPDATE_PEER_GROUP_URL,
      params: { id: formMode.id },
    }
  }

  return null
}

const getManualAccountUrl = (state: State): typeof CREATE_MANUAL_ACCOUNT_URL | UrlWithId<typeof EDIT_MANUAL_ACCOUNT_URL> | null => {
  const session = linkageSessionSelector(state)
  if (!session) {
    return null
  }

  const manualBalanceAccount = createManualBalanceAccountSelector()(state, { cid: session.cid })
  if (!manualBalanceAccount) {
    return CREATE_MANUAL_ACCOUNT_URL
  }

  return {
    url: EDIT_MANUAL_ACCOUNT_URL,
    params: {
      id: manualBalanceAccount.id,
    },
  }
}

const createPatchTransactionUrl = (type: FullMoneyflowType | 'floating-pane') => (state: State): UrlWithId<typeof PATCH_TRANSACTION_URL> | null => {
  if (type === 'floating-pane') {
    const pane = getItemFromCollection(state.app.floatingPanes.panes, 'transaction-recat') as TransactionRecatFloatingPane | null
    if (!pane || !pane.transaction) {
      return null
    }

    return {
      url: PATCH_TRANSACTION_URL,
      params: {
        id: pane.transaction.uid,
      },
    }
  }

  const { editingId } = state.app[type].transactions
  if (!editingId) {
    return null
  }

  return {
    url: PATCH_TRANSACTION_URL,
    params: {
      id: editingId,
    },
  }
}

const FORM_URLS: Partial<Record<FormNameType, GetFormUrlFunction>> = {
  // peer group
  [FORM_CREATE_CUSTOM_PEER_GROUP]: () => CREATE_PEER_GROUP_URL,
  [FORM_EDIT_CUSTOM_PEER_GROUP]: getUpdatePeergroupUrl,

  // onboarding
  [FORM_ONBOARDING_REGISTER]: () => CREATE_MEMBER_URL,
  [FORM_ONBOARDING_PROPERTY]: () => SET_MEMBER_ASSETS_URL,

  // manual accounts
  [FORM_MANUAL_ACCOUNT]: getManualAccountUrl,

  // settings credit
  [FORM_SETTINGS_CREDIT]: () => SET_MEMBER_CREDIT_URL,
  [FORM_SETTINGS_CREDIT_EXTENDED]: () => SET_MEMBER_CREDIT_EXTENDED_URL,

  // until actual login
  [FORM_LOGIN]: () => LOGIN_MEMBER_URL,
  [FORM_DEVICE_VERIFICATION]: () => LOGIN_MEMBER_URL,
  [FORM_PASSWORD_CODE_SEND]: () => SEND_PASSWORD_RESET_CODE_URL,
  [FORM_PASSWORD_RESET]: () => RESET_PASSWORD_URL,

  // settings
  [FORM_MEMBER_PROFILE]: () => null,
  [FORM_CONTACT_EDIT]: () => PATCH_MEMBER_INFO_URL,
  [FORM_PASSWORD_CHANGE]: () => CHANGE_PASSWORD_URL,

  [FORM_HOMES_EDIT]: createPropertyGetUrl('home'),
  [FORM_CARS_EDIT]: createPropertyGetUrl('car'),

  // trackers
  [FORM_CREATE_SPENDING_TRACKER]: createTrackerGetUrl('spending'),
  [FORM_CREATE_INCOME_TRACKER]: createTrackerGetUrl('income'),

  // transactions
  [FORM_EDIT_SPENDING_TRANSACTION]: createPatchTransactionUrl('spending'),
  [FORM_EDIT_INCOME_TRANSACTION]: createPatchTransactionUrl('income'),
  [FORM_EDIT_TRANSACTIONS_TRANSACTION]: createPatchTransactionUrl('transactions'),

  // bounty
  [FORM_REDEMPTION]: () => CREATE_REDEMPTION_URL,

  // feed
  [FORM_SOCIAL_PROFILE]: () => POST_SOCIAL_PROFILE_URL,
}

export type TransformationFunction<TFormValues = AnyDict, TRawFormValues = AnyDict> = (values: TFormValues, state: State) => TRawFormValues | null
const transformTrackerValues: TransformationFunction = (values) => {
  const { category } = values
  return {
    ...values,
    category: resolveCategory(category, true),
  }
}

const transformPeergroupValues: TransformationFunction = (values) => {
  const { peergroup } = values

  return {
    ...values,
    peergroup: preparePeergroupQuery(peergroup),
  }
}

const transformCityValues: TransformationFunction = (values) => ({
  ...values,
  city: values.city.value,
})

const transformMemberProfile: TransformationFunction = (value, state) => {
  const { avatar, ...nextValue } = transformCityValues(value, state) as { avatar: ImageId }

  return {
    ...nextValue,
    ...avatar ? { avatar } : {},
  }
}

const transformPhoneValues: TransformationFunction = (values) => ({
  ...values,
  phone: values.phone !== '' ? '+1' + values.phone : '',
})

const transformOnboardingRegister: TransformationFunction<RegisterFormValues, RawRegisterFormValues> = (values, state) => {
  const { search } = state.app.navigation.initialLocation
  const { policy, form_mode, ...restValues } = values

  const searchWithoutQuestionMark = search.replace(/^\?/, '')

  const playerId = pushNotificationsPlayerIdSelector(state)
  const rawValues: RawRegisterFormValues = {
    ...restValues,
    ...(playerId ? { player_id: playerId } : null),
    ...(searchWithoutQuestionMark ? { tracking: searchWithoutQuestionMark } : null),
    ...(form_mode === 'credit-card' ? { join_credit_card_waitlist: true } : null),
  }
  return rawValues
}

const transformOnboardingProperty: TransformationFunction = (values) => {
  if (values.housing_type === 'ownership') {
    const { zip5, form_mode, ...restValues } = values
    const nextHomes: RawHomeFormValues[] = values.homes.map(transformCityValues)
    return {
      ...restValues,
      homes: nextHomes,
    }
  }

  {
    const { homes, form_mode, ...restValues } = values
    return restValues
  }
}

const transformTransactionEdit: TransformationFunction = (values: TransactionEditFormValues): PatchTransactionAPIPayload | null => {
  const { amount, description, date, category } = values

  const parsedAmount = parseCurrency(amount)
  if (!parsedAmount) {
    return null
  }

  return {
    transaction: {
      amount: parsedAmount,
      description,
      date: moment(date, DATE_FORMAT_US).format(DATE_FORMAT_ISO_FULL),
      category: category.id,
    },
    apply_to_ids: [],
    create_rule: false,
  }
}

const transformRedemption: TransformationFunction = (values: RedemptionFormValues): PatchRedemptionFormValues => {
  const { amount, remember, account_number, routing_number, name, bank_account_type } = values
  return {
    account: {
      ...(remember ? { name } : null),
      bank_account_type,
      account_number: account_number.replace(NO_DIGITS_REGEX, ''),
      routing_number: routing_number.replace(NO_DIGITS_REGEX, ''),
    },
    amount: amount.toFixed(2),
    remember_account: remember,
  }
}

const transformPasswordReset: TransformationFunction = (values, state: State) => {
  const playerId = pushNotificationsPlayerIdSelector(state)
  if (!playerId) {
    return values
  }

  return {
    ...values,
    player_id: playerId,
  }
}

const rawManualAccountForm = (values: ManualAccountFormData): RawManualAccountFormData => {
  if (!values.category || values.amount.absValue === null) {
    throw new Error(`Cannot send empty category or empty amount to server. This case should be covered by validation.`)
  }

  return {
    name: values.name.trim(),
    amount: values.amount.sign * values.amount.absValue,
    category: values.category,
  }
}

// TODO make forms typed
export const FORM_TRANSFORMATIONS: Partial<Record<FormNameType, TransformationFunction>> = {
  [FORM_ONBOARDING_REGISTER]: transformOnboardingRegister,
  [FORM_ONBOARDING_PROPERTY]: transformOnboardingProperty,
  [FORM_SETTINGS_CREDIT]: transformCityValues,
  [FORM_SETTINGS_CREDIT_EXTENDED]: transformCityValues,
  [FORM_MANUAL_ACCOUNT]: rawManualAccountForm,
  [FORM_HOMES_EDIT]: transformCityValues,
  [FORM_CREATE_SPENDING_TRACKER]: transformTrackerValues,
  [FORM_CREATE_INCOME_TRACKER]: transformTrackerValues,
  [FORM_CREATE_CUSTOM_PEER_GROUP]: transformPeergroupValues,
  [FORM_EDIT_CUSTOM_PEER_GROUP]: transformPeergroupValues,
  [FORM_MEMBER_PROFILE]: transformMemberProfile,
  [FORM_CONTACT_EDIT]: transformPhoneValues,
  [FORM_EDIT_SPENDING_TRANSACTION]: transformTransactionEdit,
  [FORM_EDIT_INCOME_TRANSACTION]: transformTransactionEdit,
  [FORM_EDIT_TRANSACTIONS_TRANSACTION]: transformTransactionEdit,
  [FORM_REDEMPTION]: transformRedemption,
  [FORM_PASSWORD_RESET]: transformPasswordReset,
}

function getValues (formName: FormNameType, values: AnyDict, state: State): AnyDict | null {
  const transformation = FORM_TRANSFORMATIONS[formName]
  return transformation ? transformation(values, state) : values
}

type PostSubmitActionGenerator = (response: any, request: any) => Action

// untransformed means form values is not converted to BE format yet
const SUBMIT_POST_UNTRANSFORMED_ACTIONS: Partial<Record<FormNameType, PostSubmitActionGenerator>> = {}

// transformed means form values is converted to BE format
const SUBMIT_POST_TRANSFORMED_ACTIONS: Partial<Record<FormNameType, PostSubmitActionGenerator>> = {
  [FORM_EDIT_CUSTOM_PEER_GROUP]: updatePeergroupRelationActionCreator,
}

const CATCH_FUNCTIONS: Partial<Record<FormNameType, CatchFunction>> = {
  [FORM_SETTINGS_CREDIT]: processTUCIError,
  [FORM_SETTINGS_CREDIT_EXTENDED]: processTUCIError,
}

const submitFormEpic: Epic = (action$, store) =>
  action$
    .pipe(byType(SUBMIT_FORM))
    .pipe(mergeMapSafe((action) => {
      const { formName, values, extra } = action.payload

      const state = store.getState()
      const getFormUrl = FORM_URLS[formName]
      if (!getFormUrl) {
        captureWarning(NO_FORM_HANDLER_FOUND, {
          formName,
        })

        return empty()
      }
      const endpoint = getFormUrl(state, values)
      if (!endpoint) {
        return empty()
      }
      const transformedValues = getValues(formName, values, state)
      if (!transformedValues) {
        return empty()
      }

      return concat(
        of(startSubmit(formName)),
        post(endpoint, { body: transformedValues }).pipe(
          mergeMap(({ response }) => {
            const formSubmittedAction = formSubmittedSuccessfullyActionCreator(
              formName,
              response,
              values,
              extra,
            )
            const actions: Action[] = []

            const postSubmitUntransformedActionCreator = SUBMIT_POST_UNTRANSFORMED_ACTIONS[formName]
            if (postSubmitUntransformedActionCreator) {
              const postSubmitUntransformedAction = postSubmitUntransformedActionCreator(response, values)
              if (postSubmitUntransformedAction) {
                actions.push(postSubmitUntransformedAction as Action)
              }
            }

            const postSubmitTransformedActionCreator = SUBMIT_POST_TRANSFORMED_ACTIONS[formName]
            if (postSubmitTransformedActionCreator) {
              const postSubmitTransformedAction = postSubmitTransformedActionCreator(response, transformedValues)
              if (postSubmitTransformedAction) {
                actions.push(postSubmitTransformedAction as Action)
              }
            }

            actions.push(formSubmittedAction, stopSubmit(formName))

            return from(actions)
          }))
          .pipe(catchSafe((error: AjaxError) => {
            const { status, xhr: { response }} = error

            const formSubmitFailedAction = formSubmitFailedActionCreator(formName)

            if (status === VALIDATION_ERROR_STATUS_CODE) {
              const responseErrors: FormErrorsResponse = typeof response === 'string' ? JSON.parse(response) : response
              return from([
                stopSubmit(formName, getResponseFormErrors(responseErrors || {})),
                formSubmitFailedAction,
              ])
            }

            const stopSubmitAction = stopSubmit(formName, {
              _error: [`Unexpected error: ${ error.status }`],
            })

            const catchFunction = CATCH_FUNCTIONS[formName]
            if (catchFunction) {
              return merge(
                of(stopSubmitAction),
                of(formSubmitFailedAction),
                catchFunction(error),
              )
            }

            return of<CompositeAction>(
              stopSubmitAction,
              formSubmitFailedAction,
              setUnexpectedErrorActionCreator(getErrorType(error), error),
            )
          })),
      )
    }))

export default combineEpics(
  submitFormEpic,
)
